import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentPropsWithRef, ReactElement } from 'react';
import { cn } from '../util/classes.ts';

/**
 * Appearance of {@link Avatar}: a round, initials-sized badge and the
 * `variant` colour pairs. Exported so components that wrap an avatar in a
 * different element (a trigger button, a menu row) can reuse the look.
 */
export const avatarVariants = cva(
    [
        'inline-flex shrink-0 select-none items-center justify-center rounded-full',
        'size-8 text-caption font-semibold',
    ],
    {
        variants: {
            variant: {
                // a person in a different mode than the default, e.g. viewing a colleague's calendar
                primary: 'bg-brand/(--opacity-wash) text-brand',
                // no avatar fill token yet, neutral-200 matches the calendar redesign mock
                secondary: 'bg-neutral-200 text-content',
            },
        },
        defaultVariants: {
            variant: 'secondary',
        },
    },
);

/**
 * Property type for {@link Avatar}.
 */
export type AvatarProps = ComponentPropsWithRef<'span'> & VariantProps<typeof avatarVariants>;

/**
 * A round badge showing a person's initials. The caller passes the initials
 * as children, so deriving them from a name stays with the app. Size comes
 * from the caller: 32px by default, override with a `size-*` class.
 *
 * ```tsx
 * <Avatar>TW</Avatar>
 * <Avatar variant="primary" className="size-10">SF</Avatar>
 * ```
 */
export function Avatar({ className, variant, ...props }: AvatarProps): ReactElement {
    return <span data-slot="avatar" className={cn(avatarVariants({ variant }), className)} {...props} />;
}
